/* eslint-disable */
import React, { useState } from 'react';
import { Box, TextField, Button, Alert, CircularProgress, Chip } from '@mui/material';
import { Search, Refresh } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getRelease, refreshRelease } from '../../services/api';
import type { ReleaseResponse } from '../../types/release';

interface ReleaseFormProps {
  onReleaseGenerated: (data: ReleaseResponse) => void;
}

const RELEASE_PATTERN = /^\d{2}\.\d{2}$/;

export const ReleaseForm: React.FC<ReleaseFormProps> = ({ onReleaseGenerated }) => {
  const [release, setRelease] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);

  // Query for getting release (checks cache first via GET endpoint)
  const releaseQuery = useQuery({
    queryKey: ['release', release],
    queryFn: () => getRelease(release),
    enabled: false, // Don't auto-fetch, only on manual trigger
    retry: false,
    staleTime: 0, // Always consider stale to allow refresh
  });

  // Mutation for refreshing release
  const refreshMutation = useMutation({
    mutationFn: (releaseValue: string) => refreshRelease(releaseValue),
    onSuccess: (data) => {
      setError(null);
      setIsFromCache(false);
      // Update query cache with fresh data
      releaseQuery.refetch();
      onReleaseGenerated(data);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to refresh release content');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!release.trim()) {
      setError('Please enter a release identifier');
      return;
    }

    if (!RELEASE_PATTERN.test(release.trim())) {
      setError('Release format must be YY.MM (e.g., 26.01)');
      return;
    }

    // Use GET endpoint which checks cache first, generates if needed
    releaseQuery.refetch().then((result) => {
      if (result.isSuccess && result.data) {
        // Assume it's from cache if query was fast (heuristic)
        // In a real implementation, you might want to track this server-side
        setIsFromCache(true);
        onReleaseGenerated(result.data);
      }
    }).catch((err: Error) => {
      setError(err.message || 'Failed to get release content');
      setIsFromCache(false);
    });
  };

  const handleRefresh = () => {
    if (!release.trim() || !RELEASE_PATTERN.test(release.trim())) {
      return;
    }
    refreshMutation.mutate(release.trim());
  };

  const isLoading = refreshMutation.isPending || releaseQuery.isFetching;

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2, mb: 5 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: { xs: 'stretch', md: 'flex-start' },
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        <TextField
          label="Release Identifier"
          placeholder="26.01"
          variant="outlined"
          value={release}
          onChange={(e) => {
            setRelease(e.target.value);
            setIsFromCache(false);
          }}
          error={!!error}
          helperText={error || 'Format: YY.MM (e.g., 26.01 for January 2026)'}
          disabled={isLoading}
          sx={{
            flexGrow: 1,
            minWidth: { xs: '100%', lg: 420 },
            '& .MuiFormHelperText-root': { mt: 1 },
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ pattern: RELEASE_PATTERN.source }}
        />
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', pt: { xs: 0, lg: 0.25 } }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} /> : <Search />}
            disabled={isLoading || !release.trim()}
            sx={{ minWidth: 150, height: 40 }}
          >
            {isLoading ? 'Loading...' : 'Generate'}
          </Button>
          {release.trim() && RELEASE_PATTERN.test(release.trim()) && (
            <Button
              type="button"
              variant="outlined"
              startIcon={refreshMutation.isPending ? <CircularProgress size={20} /> : <Refresh />}
              disabled={isLoading}
              onClick={handleRefresh}
              sx={{ minWidth: 120, height: 40 }}
            >
              {refreshMutation.isPending ? 'Refreshing...' : 'Refresh'}
            </Button>
          )}
        </Box>
      </Box>
      
      {isFromCache && !isLoading && (
        <Box sx={{ mt: 1.5 }}>
          <Chip 
            label="Cached" 
            size="small" 
            color="info" 
            variant="outlined"
          />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
